import { Injectable } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { Observable } from "rxjs";
import { apiCallWrapper } from "../api/api.util";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import { HasId } from "../model/generics";
import { CustomerProductApi } from "../api/customerproduct.api";
import { ProductCustomer } from "../model/ddb.model";
import { LoadingService } from "./core/loading.service";
import { ICustomerBackorderProductLog } from "../model/customer-backorder-product.model";

@Injectable()
export class CustomerProductService {
  constructor(
    private notifications: NotificationsService,
    private customerProductApi: CustomerProductApi,
    private loadingService: LoadingService,
  ) {}

  public list(
    query: IQueryFilter,
    showNotification = true,
  ): Observable<QueryResult<ProductCustomer & HasId<string>>> {
    return apiCallWrapper(
      this.customerProductApi.list(query),
      showNotification
        ? {
            notificationsService: this.notifications,
            action: "Fetching customer product",
          }
        : undefined,
    );
  }

  public get(id: number): Observable<ProductCustomer & HasId<string>> {
    return apiCallWrapper(this.customerProductApi.get(id));
  }

  public update(id: string, data: any): Observable<any> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.update(id, data),
      ),
      {
        notificationsService: this.notifications,
        action: "Updating Customer Product",
      },
    );
  }

  public create(data: ProductCustomer) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.create(data),
      ),
      {
        notificationsService: this.notifications,
        action: "Create customer user",
      },
    );
  }

  public delete(id: number): Observable<{ success: boolean; deleted: number }> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.delete(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Delete Customer Product",
      },
    );
  }

  public getAllProductCustomersByProductId(
    id: number | string,
  ): Observable<ProductCustomer[]> {
    return apiCallWrapper(
      this.customerProductApi.getAllProductCustomersByProductId(id),
      {
        notificationsService: this.notifications,
        action: "Fetching customer product",
      },
    );
  }

  public submitSubstituteRequest(payload: {
    id: number;
    substituteProductId: number | null;
    substituteRule: string | null;
    note: string;
    status?: string;
  }): Observable<any> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.submitSubstituteRequest(payload),
      ),
      {
        notificationsService: this.notifications,
        action: "Submitting substitute request",
      },
    );
  }

  public approveSubstituteAction(
    id: number | string,
    type: string,
    rule?: string,
    info?: string,
  ): Observable<any> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.approveSubstituteAction(id, type, rule, info),
      ),
      {
        notificationsService: this.notifications,
        action: "Processing substitute action",
      },
    );
  }

  public getSubstituteLogs(
    query: IQueryFilter,
  ): Observable<QueryResult<ICustomerBackorderProductLog>> {
    return apiCallWrapper(this.customerProductApi.getSubstituteLogs(query), {
      notificationsService: this.notifications,
      action: "Fetching substitute logs",
    });
  }

  public updateBulkSubstituteproduct(
    data: {
      id: number | string;
      status?: string;
      rule?: string;
    }[],
  ): Observable<any> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.updateBulkSubstituteproduct(data),
      ),
      {
        notificationsService: this.notifications,
        action: "Processing substitute action",
      },
    );
  }

  public bulkSendSubstituteApproval(ids: (number | string)[]): Observable<any> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.bulkSendSubstituteApproval(ids),
      ),
      {
        notificationsService: this.notifications,
        action: "Sending substitute approval request",
      },
    );
  }

  public generateSubstituteReport(payload: {
    customerId: string | number;
    id?: (string | number)[];
  }): Observable<{ s3url: string }> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.customerProductApi.generateSubstituteReport(payload),
      ),
      {
        notificationsService: this.notifications,
        action: "Generating substitute report",
      },
    );
  }
}
